import React from 'react'
import PropTypes from 'prop-types'
import { dataProps } from '@ds/react-utils'
import { logAlphaComponent } from '../../logging'
import styles from './styles'

export const justifyContentValues = [
  'flex-start',
  'center',
  'flex-end',
] as const
export type FlexRowJustifyContent = (typeof justifyContentValues)[number]

export interface FlexRowProps {
  /**
   * The 'children' prop accepts arbitrary nodes.
   */
  children: React.ReactNode
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * The space between each content item.
   */
  gap?: number
  /**
   * This tells the flex container to grow each content item to fill available space.
   */
  grow?: boolean
  /**
   * The justify-content property. It defines the alignment of contnent items along the X axis.
   */
  justifyContent?: FlexRowJustifyContent
  /**
   * Minium width of the content item. Required if the items do not have a set width.
   */
  minimumItemWidth?: number
}

export function FlexRow({
  children,
  gap,
  grow = false,
  justifyContent,
  minimumItemWidth,
  ...restProps
}: FlexRowProps & React.HTMLProps<HTMLDivElement>) {
  logAlphaComponent('FlexRow')

  const flexBasisStyle = {
    '& > *': minimumItemWidth
      ? { flexBasis: `${minimumItemWidth}px` }
      : { flexBasis: 'auto' },
  }

  const flexGrowStyle = grow && {
    '& > *': {
      flexGrow: 1,
    },
  }

  const justifyContentStyle = justifyContent && {
    justifyContent: `${justifyContent}`,
  }

  const marginStyle = gap && {
    margin: `-${gap}px 0 0 -${gap}px`,
    '& > *': {
      margin: `${gap}px 0 0 ${gap}px`,
    },
  }

  const maxWidthStyle = gap && {
    '& > *': {
      maxWidth: `calc(100% - ${gap}px)`,
    },
  }

  const flexrowStyles = [
    styles.FlexRow,
    flexBasisStyle,
    flexGrowStyle,
    justifyContentStyle,
    marginStyle,
    maxWidthStyle,
  ]

  return (
    <div {...dataProps(restProps)} css={flexrowStyles} {...restProps}>
      {children}
    </div>
  )
}

FlexRow.justifyContentValues = justifyContentValues

FlexRow.propTypes = {
  children: PropTypes.node.isRequired,
  'data-.*': PropTypes.string,
  gap: PropTypes.number,
  grow: PropTypes.bool,
  justifyContent: PropTypes.oneOf(justifyContentValues),
  minimumItemWidth: PropTypes.number,
}

FlexRow.displayName = 'FlexRow'
